// Why a photo could not be taken, as recorded with the login attempt
const cameraError = (err) =>
    ({
        NotAllowedError: "camera permission denied",
        SecurityError: "camera blocked by the browser",
        NotFoundError: "no camera found",
        NotReadableError: "camera in use or not working",
        OverconstrainedError: "camera does not support the requested size",
        AbortError: "camera stopped",
    }[err?.name] || err?.message || "camera unavailable");

// The browser's camera permission for this page: "granted", "denied", "prompt", or "" when it cannot tell
const cameraPermission = async () => {
    try {
        return (await navigator.permissions.query({ name: "camera" })).state;
    } catch (err) {
        return "";
    }
};

/**
 * Asks for the camera as the login page opens, so the browser's "Allow camera" question comes up before the
 * candidate types anything rather than while the login waits for a picture. The camera goes off at once.
 * Nothing happens when it is already allowed. Never rejects.
 */
export const askCameraPermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    if ((await cameraPermission()) === "granted") return;
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
        // denied or no camera: the login photo records why
    }
};

const WARM_UP_MS = 800; // first frames come out dark while the camera adjusts its exposure
const TIMEOUT_MS = 6000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Login photo (a security record; nothing is shown to the candidate). The camera is switched on only when a
 * login step is submitted, one frame is taken, and it is switched off again. Resolves to { photo } as a
 * 480 px wide JPEG data URL, or { photo_error } saying why there is none. Never rejects.
 */
export const takeLoginPhoto = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return { photo_error: "camera not available in this browser" };
    let stream;
    const video = document.createElement("video");
    try {
        const capture = (async () => {
            stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
            video.muted = true;
            video.playsInline = true;
            video.srcObject = stream;
            await video.play();
            await sleep(WARM_UP_MS);
            if (!video.videoWidth) throw new Error("camera gave no picture");
            const width = 480;
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = Math.round((video.videoHeight / video.videoWidth) * width);
            canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
            return { photo: canvas.toDataURL("image/jpeg", 0.75) };
        })().catch((err) => ({ photo_error: cameraError(err) }));
        // Still waiting for the candidate to answer the browser's camera question counts as not allowed
        const timeout = sleep(TIMEOUT_MS).then(async () => {
            const permission = await cameraPermission();
            return { photo_error: permission === "prompt" ? "camera permission not given" : permission === "denied" ? "camera permission denied" : "camera did not respond in time" };
        });
        return await Promise.race([capture, timeout]);
    } finally {
        // The race may finish on the timeout first; stop the camera whenever it did start
        const stop = () => stream && stream.getTracks().forEach((t) => t.stop());
        stop();
        setTimeout(stop, TIMEOUT_MS);
        video.srcObject = null;
    }
};
